import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { agentApi, ClientSummary } from '../services/agentApi';
import { Building2, ArrowLeft, Heart, Eye, X, Copy, Check } from 'lucide-react';

function DetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-6 bg-muted rounded w-1/3" />
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-muted rounded-xl" />)}
      </div>
      <div className="space-y-2">
        {[1, 2, 3, 4].map((i) => <div key={i} className="h-12 bg-muted rounded-xl" />)}
      </div>
    </div>
  );
}

const TYPE_ICON = {
  liked: <Heart className="w-3.5 h-3.5 text-primary fill-primary" />,
  viewed: <Eye className="w-3.5 h-3.5 text-muted-foreground" />,
  rejected: <X className="w-3.5 h-3.5 text-destructive" />,
};

const TYPE_LABEL = { liked: 'Favorita', viewed: 'Vista', rejected: 'Descartada' };

function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    agentApi.getClientSummary(clientId)
      .then(setSummary)
      .catch(() => setError('No se pudo cargar el resumen del cliente'))
      .finally(() => setLoading(false));
  }, [clientId]);

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate('/agent/clients')}
            aria-label="Volver"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm truncate">
              {summary?.client.name ?? 'Detalle de cliente'}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {loading && <DetailSkeleton />}
        {error && <p className="text-sm text-destructive">{error}</p>}

        {summary && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { icon: <Eye className="w-4 h-4" />, label: 'Vistas', value: summary.total_viewed },
                { icon: <Heart className="w-4 h-4 text-primary" />, label: 'Favoritas', value: summary.total_liked },
                { icon: <X className="w-4 h-4 text-destructive" />, label: 'Descartadas', value: summary.total_rejected },
              ].map(({ icon, label, value }) => (
                <div key={label} className="border border-border rounded-xl px-4 py-3">
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-1">{icon}<span className="text-xs">{label}</span></div>
                  <p className="text-2xl font-semibold">{value}</p>
                </div>
              ))}
            </div>

            {/* Preferences */}
            {summary.preferences && (
              <div className="mb-8">
                <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium mb-3">
                  Preferencias de búsqueda
                </p>
                <div className="border border-border rounded-xl p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
                  {summary.preferences.price_min != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">Precio mín.</p>
                      <p className="font-medium text-sm">${summary.preferences.price_min.toLocaleString('es-MX')}</p>
                    </div>
                  )}
                  {summary.preferences.price_max != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">Precio máx.</p>
                      <p className="font-medium text-sm">${summary.preferences.price_max.toLocaleString('es-MX')}</p>
                    </div>
                  )}
                  {summary.preferences.bedrooms_min != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">Recámaras mín.</p>
                      <p className="font-medium text-sm">{summary.preferences.bedrooms_min}</p>
                    </div>
                  )}
                  {summary.preferences.bathrooms_min != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">Baños mín.</p>
                      <p className="font-medium text-sm">{summary.preferences.bathrooms_min}</p>
                    </div>
                  )}
                  {summary.preferences.area_min != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">Área mín.</p>
                      <p className="font-medium text-sm">{summary.preferences.area_min} m²</p>
                    </div>
                  )}
                  {summary.preferences.zones && summary.preferences.zones.length > 0 && (
                    <div className="col-span-2 md:col-span-3">
                      <p className="text-xs text-muted-foreground mb-1">Zonas de interés</p>
                      <div className="flex flex-wrap gap-1.5">
                        {summary.preferences.zones.map((z) => (
                          <span key={z} className="text-xs bg-muted px-2 py-0.5 rounded-full">{z}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Portal link */}
            <div className="mb-8">
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium mb-3">
                Portal de cliente
              </p>
              <div className="border border-border rounded-xl p-5 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Comparte este enlace con <strong>{summary.client.name}</strong> para que acceda a su portal de propiedades.
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm font-mono text-muted-foreground truncate select-all">
                    {`${window.location.origin}/portal/login?agency=${summary.client.agency_id}`}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/portal/login?agency=${summary.client.agency_id}`)
                        .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
                        .catch(() => {});
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted/50 transition-colors shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Email: <span className="font-mono">{summary.client.email}</span>
                </p>
              </div>
            </div>

            {/* Activity */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium mb-3">
                Actividad reciente
              </p>
              {summary.interactions.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin actividad registrada aún.</p>
              ) : (
                <div className="space-y-2">
                  {summary.interactions.map((i) => (
                    <div key={i.id} className="border border-border rounded-xl px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        {TYPE_ICON[i.type]}
                        <div>
                          <p className="text-xs font-medium">{TYPE_LABEL[i.type]}</p>
                          <p className="text-xs text-muted-foreground font-mono">{i.property_id.slice(0, 8)}…</p>
                        </div>
                      </div>
                      <time className="text-xs text-muted-foreground">
                        {new Date(i.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                      </time>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default ClientDetailPage;
